import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useDocumentHead from '../hooks/useDocumentHead';
import database from '../database';
import { processOpeningLines } from '../utils/pgnUtils';
import { findBySlug, generateLineUrl, slugify } from '../utils/urlUtils';
import OpeningTrainer from '../OpeningTrainer';

function OpeningTrainerPage() {
  const { openingSlug, lineSlug } = useParams();
  const navigate = useNavigate();
  const [opening, setOpening] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // The line is derived from the URL so switching lines doesn't reload the course
  const line = useMemo(() => {
    if (!opening || !lineSlug) return null;
    return opening.lines.find(l => slugify(l.name) === lineSlug) || null;
  }, [opening, lineSlug]);

  // Get meta data for SEO
  const getOpeningMeta = () => {
    if (!opening) return {};
    
    const title = `${opening.name} - Eseky Chess Training`;
    const description = `Learn the ${opening.name} chess opening with Eseky's interactive training platform. Master this ${opening.playerColor === 'white' ? 'White' : 'Black'} opening with ${opening.lines?.length || 0} variations and step-by-step guidance.`;
    const keywords = `eseky, ${opening.name}, chess opening, chess training, ${opening.playerColor === 'white' ? 'white' : 'black'} opening, chess strategy, chess tactics`;
    
    return { title, description, keywords };
  };

  const { title, description, keywords } = getOpeningMeta();

  // Use document head hook for SEO - must be called before any conditional returns
  useDocumentHead({
    title: title || 'Eseky - Chess Opening Training',
    description: description || 'Learn chess openings with Eseky\'s interactive training platform.',
    keywords: keywords || 'eseky, chess training, chess openings',
    canonical: line && opening ? generateLineUrl(opening, line) : opening ? `/${slugify(opening.name)}` : '/',
    ogTitle: title || 'Eseky - Chess Opening Training',
    ogDescription: description || 'Learn chess openings with Eseky\'s interactive training platform.',
    ogUrl: line && opening ? generateLineUrl(opening, line) : opening ? `/${slugify(opening.name)}` : '/',
    ogType: 'article',
    twitterTitle: title || 'Eseky - Chess Opening Training',
    twitterDescription: description || 'Learn chess openings with Eseky\'s interactive training platform.',
    structuredData: opening ? {
      "@context": "https://schema.org",
      "@type": "Course",
      "name": opening.name,
      "description": description,
      "provider": {
        "@type": "Organization",
        "name": "Eseky"
      },
      "educationalLevel": opening.difficulty || "Beginner",
      "teaches": `${opening.name} chess opening`,
      "url": opening ? `/${slugify(opening.name)}` : '/',
      "courseCode": `opening-${opening.id}`,
      "hasCourseInstance": {
        "@type": "CourseInstance",
        "courseMode": "online",
        "instructor": {
          "@type": "Organization", 
          "name": "Eseky"
        }
      }
    } : null
  });

  useEffect(() => {
    const loadOpening = async () => {
      try {
        setLoading(true);
        await database.init();
        
        // Get all openings with their lines
        const openingsWithLines = await database.getOpeningsWithLines();
        
        // Find opening by slug
        const { opening: foundOpening } = findBySlug(openingsWithLines, openingSlug);
        
        if (!foundOpening) {
          setError('Opening not found');
          return;
        }

        // Process the opening to add moves and playerMoves arrays
        setOpening(processOpeningLines(foundOpening));
      } catch (error) {
        console.error('Failed to load opening:', error);
        setError('Failed to load opening');
      } finally {
        setLoading(false);
      }
    };

    if (openingSlug) {
      loadOpening();
    }
  }, [openingSlug]);

  const refreshOpenings = async () => {
    // Refresh the current opening data
    try {
      const openingsWithLines = await database.getOpeningsWithLines();
      const { opening: foundOpening } = findBySlug(openingsWithLines, openingSlug);
      
      if (foundOpening) {
        setOpening(processOpeningLines(foundOpening));
      }
    } catch (error) {
      console.error('Failed to refresh opening:', error);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleLineChange = useCallback((newLineId) => {
    if (!opening) return;
    
    // Find the line by ID to get its name for the URL slug
    const selectedLine = opening.lines.find(l => l.id === newLineId);
    
    if (selectedLine) {
      const newUrl = generateLineUrl(opening, selectedLine);
      navigate(newUrl, { replace: true });
    } else {
      // No line selected, go to opening page
      const newUrl = `/${slugify(opening.name)}`;
      navigate(newUrl, { replace: true });
    }
  }, [opening, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen min-h-[100dvh] flex items-center justify-center" aria-busy="true">
        <div className="flex flex-col items-center gap-3 text-ink-400">
          <div className="w-10 h-10 rounded-full border-2 border-ink-700 border-t-brand-400 animate-spin" />
          <div className="text-sm font-medium">Loading the course…</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen min-h-[100dvh] flex items-center justify-center p-4">
        <div className="card p-8 text-center max-w-sm">
          <div className="text-lg font-bold text-ink-100 mb-1">{error}</div>
          <p className="text-sm text-ink-400 mb-5">The course may have been renamed or removed.</p>
          <button onClick={handleBack} className="btn-primary">Back to courses</button>
        </div>
      </div>
    );
  }

  if (!opening) {
    return null;
  }

  return (
    <div className="min-h-screen min-h-[100dvh]">
      <OpeningTrainer 
        opening={opening} 
        currentLineId={line?.id}
        onBack={handleBack}
        onLineChange={handleLineChange}
        refreshOpenings={refreshOpenings}
      />
    </div>
  );
}

export default OpeningTrainerPage;